import {
  Box,
  Card,
  CardActions,
  CardContent,
  Button,
  Chip,
  Container,
  Grid,
  LinearProgress,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import api from '../api'
import { IResultSetListDto } from '../api/resultSetApi'
import ErrorView from '../components/ErrorView'
import LoadSpinner from '../components/loadSpinner/LoadSpinner'
import { FilterSettings } from '../reducers/filterReducer'
import { IAlphaprocessState } from '../store'
import DateUtil from '../util/DateUtil'
import nothingTodo from '../images/nothing-todo.png'

interface TaskItem {
  id: number
  checklist_id: number
  title: string
  taskProgress: string
  progress: number
  information: string
  dueDate: string | Date
  location: string
}

interface Column {
  key: 'today' | 'overdue' | 'open'
  labelKey: string
  color: 'primary' | 'error' | 'warning'
  items: TaskItem[]
}

function TaskItemCard({ item }: { item: TaskItem }) {
  const { t } = useTranslation()
  const history = useHistory()
  const dueDate = item.dueDate ? new Date(item.dueDate as string) : null
  const isOverdue = dueDate && dueDate < new Date()

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, mr: 1 }}>
            {item.title}
          </Typography>
          <Typography variant="caption" sx={{ color: isOverdue ? 'error.main' : 'text.secondary', whiteSpace: 'nowrap' }}>
            {dueDate ? DateUtil.format(dueDate, 'dd.MM.yyyy') : '—'}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={item.progress}
          sx={{ mb: 0.75, height: 4, borderRadius: 2 }}
        />
        <Typography variant="caption" color="text.secondary">
          {item.taskProgress}
        </Typography>
        {item.location && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {item.location}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ pt: 0.5, pb: 1, px: 2 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => history.push(`/checklists/${item.id}?ref=home`)}
        >
          {t('common.open')}
        </Button>
      </CardActions>
    </Card>
  )
}

function TaskColumn({ column }: { column: Column }) {
  const { t } = useTranslation()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t(column.labelKey)}
        </Typography>
        <Chip
          label={column.items.length}
          size="small"
          color={column.color}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
        {column.items.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 4, color: 'text.secondary' }}>
            <img src={nothingTodo} alt="" style={{ width: 80, opacity: 0.5 }} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {column.key === 'overdue' ? t('mobileHome.noOverdue') : t('mobileHome.allDoneLong')}
            </Typography>
          </Box>
        ) : (
          column.items.map((item) => <TaskItemCard key={item.id} item={item} />)
        )}
      </Box>
    </Box>
  )
}

export default function HomeDesktop() {
  const { t } = useTranslation()
  const filter = useSelector<IAlphaprocessState, FilterSettings>(
    (state) => state.filter.checklistFilterSettings
  )
  const [items, setItems] = useState<TaskItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const { data: resultSets } = await api.resultSets.due({
        page: 1,
        tester_ids: filter.selectedTesters.map((x) => x.value),
        contact_id: filter.selectedContacts.map((x) => x.value),
        title: filter.term,
        ressource_id: filter.selectedRessources.map((x) => x.value),
        project_id: filter.selectedProjects.map((x) => x.value),
        startDate: filter.startDate ? filter.startDate.toISOString() : null,
        endDate: filter.endDate ? filter.endDate.toISOString() : null,
        team_ids: filter.selectedTeams.map((x) => x.value),
        items: 100,
        direction: 'desc',
        sort: 'due_date'
      })

      const mapped: TaskItem[] = resultSets.map((rs: IResultSetListDto) => ({
        id: rs.id,
        checklist_id: rs.checklist_id,
        title: rs.title,
        taskProgress: t('resultSet.tasksOpenInXAreas', {
          tasksOpen: rs.tasks_remaining,
          areasCount: rs.area_count
        }),
        progress: rs.percentage,
        information: rs.description || '',
        dueDate: rs.due,
        location: rs.location
      }))

      setItems(mapped)
    } catch (err) {
      setError(err as Error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [
    filter.term,
    filter.selectedRessources,
    filter.selectedProjects,
    filter.selectedTeams,
    filter.selectedContacts,
    filter.selectedTesters,
    filter.startDate,
    filter.endDate
  ])

  if (isLoading) return <LoadSpinner className="centered" />
  if (error) return <ErrorView error={error as any} />

  const todayItems = items.filter((i) => DateUtil.isToday(i.dueDate))
  const overdueItems = items.filter((i) => DateUtil.isAfter(new Date(), i.dueDate))
  const openItems = items

  const columns: Column[] = [
    { key: 'today', labelKey: 'mobileHome.todoToday', color: 'primary', items: todayItems },
    { key: 'overdue', labelKey: 'mobileHome.overDue', color: 'error', items: overdueItems },
    { key: 'open', labelKey: 'mobileHome.open', color: 'warning', items: openItems }
  ]

  return (
    <Container maxWidth="xl" sx={{ pt: 2, pb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('menu.myTasks')}
        </Typography>
      </Box>
      <Grid container spacing={2} sx={{ height: 'calc(100vh - 180px)' }}>
        {columns.map((column) => (
          <Grid item xs={12} md={4} key={column.key} sx={{ height: '100%' }}>
            <TaskColumn column={column} />
          </Grid>
        ))}
      </Grid>
    </Container>
  )
}
